import * as React from "react";
import { RefreshCw } from "lucide-react";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { fetchSmartLinkProducts } from "../../lib/api/smart-link";
import type { SmartLinkProductLink } from "../../lib/api/smart-link";
import type { ListParams } from "../../../ui/api/listApi.types";
import { createSmartLinkProductsTableColumns } from "./smartLinkProductsTableColumns";
import { SmartLinkProductFullSyncModal } from "./SmartLinkProductFullSyncModal";
import { SmartLinkProductCompareModal } from "./SmartLinkProductCompareModal";
import { SmartLinkHealthSection } from "./SmartLinkHealthSection";
import * as pageStyles from "../users/UsersListPage.css";

export function SmartLinkProductsTab({ token }: { token: string }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [showFullSyncModal, setShowFullSyncModal] = React.useState(false);
  const [compareLink, setCompareLink] = React.useState<SmartLinkProductLink | null>(null);
  const refresh = React.useCallback(() => setRefreshKey((k) => k + 1), []);

  const columns = React.useMemo(() => createSmartLinkProductsTableColumns(t), [t]);

  const fetchLinks = React.useCallback(
    async (params: ListParams) => {
      try {
        const result = await fetchSmartLinkProducts(token, params);
        setLoadError(null);
        return result;
      } catch (err: any) {
        setLoadError(err?.message || t("pro.system.smartLink.products.error.load"));
        return { data: [], total: 0 };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshKey, t],
  );

  return (
    <>
      <SmartLinkHealthSection entity="products" token={token} refreshKey={refreshKey} />

      {loadError && (
        <div className={pageStyles.errorWrap}>
          <Callout variant="error">{loadError}</Callout>
        </div>
      )}

      <div className={pageStyles.tableHost}>
        <ListView<SmartLinkProductLink>
          listId="smart-link-products"
          userId={user?.id ?? "anonymous"}
          columns={columns}
          fetchList={fetchLinks}
          contextKey={String(refreshKey)}
          fillContainer
          enabledViews={["table"]}
          searchPlaceholder={t("pro.system.smartLink.products.search")}
          emptyTitle={t("pro.system.smartLink.products.emptyTitle")}
          emptySubtitle={t("pro.system.smartLink.products.emptySubtitle")}
          defaultSort={{ sort_by: "lastSyncedAt", order: "desc" }}
          onRowClick={(row) => setCompareLink(row)}
          headerActions={[
            {
              id: "full-sync",
              label: t("pro.system.smartLink.fullSync.button"),
              icon: <RefreshCw size={14} />,
              onClick: () => setShowFullSyncModal(true),
            },
          ]}
        />
      </div>

      {showFullSyncModal && (
        <SmartLinkProductFullSyncModal open={showFullSyncModal} setOpen={setShowFullSyncModal} onCommitted={refresh} />
      )}

      {compareLink && (
        <SmartLinkProductCompareModal
          productId={compareLink.productId}
          productName={compareLink.product.name}
          linkEnabled={compareLink.linkEnabled}
          open={Boolean(compareLink)}
          setOpen={(open) => {
            if (!open) setCompareLink(null);
          }}
          onSynced={refresh}
        />
      )}
    </>
  );
}
